import { isWithinInterval } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Platform } from 'react-native';

import { appVersion } from 'utils/app-version-info';

import { ScreenOrientation } from '../types';

export { downloadFileFromUrl } from './download-file-from-url';
export { handleShare } from './handle-share';
export { saveFile } from './save-file';
export { extractNameAndFileType, getBase64Type, getFileExtension, isBase64 } from './shared-utils';

export const appMessages = {
  // If no jwt is provided, the message will be 'APP::USER::' and it's up to the caller to handle that as a no user logged in.
  user: (jwt: string) => `APP::USER::${jwt || ''}`,
  // Generate a message to send to the webview to indicate that the app is loading.
  loading: (isLoading: boolean) => `APP::LOADING::${isLoading}`,
  device: (deviceId: string) => `APP::DEVICE::${deviceId}`,
  sensorData: (sensorData: string) => `APP::SENSOR_DATA::${sensorData}`,
  sensorsAvailability: (sensorsAvailable: boolean) => `APP::SENSORS_AVAILABLE::${sensorsAvailable}`,
  platform: `APP::PLATFORM::${Platform.OS}::VERSION::${appVersion}`,
  screenOrientation: (orientation: ScreenOrientation) => `APP::SCREEN_ORIENTATION::${orientation}`,
  localStorage: (localStorage: string) => `APP::LOCAL_STORAGE::${localStorage}`,
  sendCameraResult: (photo: string) => `APP::CAMERA_IMAGE::${photo}`,
};

/**
 * Determines if the user's current time falls within a specified date range in a target timezone.
 *
 * This function takes startDateString and endDateString in 'YYYY-MM-DD' format from Sanity, representing dates in the
 * target timezone ie:(America/Argentina/Buenos_Aires).
 * The function then compares the user's current time, adjusted to the target timezone, against this date range.
 *
 * @param {string} startDateString - The start date of the range in 'YYYY-MM-DD' format (e.g., '2023-11-30').
 * @param {string} endDateString - The end date of the range in 'YYYY-MM-DD' format (e.g., '2023-11-30').
 * @return {boolean} - Returns true if the user's current time (in their local timezone) falls within
 *                     the specified date range in the target timezone. Returns false otherwise.
 */
export const isCampaignActive = ({
  startDateString,
  endDateString,
  timeZone,
}: {
  startDateString: string;
  endDateString: string;
  timeZone: string;
}): boolean => {
  // We are not keeping exact schedules at Sanity currently. Adding the schedules allows us to take the entire day into consideration.
  const startDate = new Date(`${startDateString}T00:00:00`);
  const endDate = new Date(`${endDateString}T23:59:59`);
  const userCurrentDate = new Date();

  // Convert user's current UTC time to target timezone
  const userCurrentInCampaignTimeZone = utcToZonedTime(userCurrentDate, timeZone);

  const isInRange = isWithinInterval(userCurrentInCampaignTimeZone, {
    start: startDate,
    end: endDate,
  });

  return isInRange;
};

// This is the default values for marketing campaigns
// where we assume that photo will be taken in portrait and not landscape
// and default values are that. Max size is 3, with a compression of 0.8 and format
// is jpeg.
const defaultValues = {
  format: 'jpeg',
  maxSize: 3,
  maxWidth: 1080,
  maxHeight: 1920,
};

const parseCameraOptions = (options?: string) => {
  if (!options) {
    return defaultValues;
  }
  try {
    return JSON.parse(options);
  } catch (error) {
    return defaultValues;
  }
};

export const normalizeCameraOptions = (options: string) => {
  return parseCameraOptions(options);
};
